// contexts/alfaroojfresh/PurchaseOrdersContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAppCache } from "../../hooks/Cachecontext";

const PurchaseOrdersContext = createContext();

export const usePurchaseOrders = () => {
  const context = useContext(PurchaseOrdersContext);
  if (!context) throw new Error("usePurchaseOrders must be used within a PurchaseOrdersProvider");
  return context;
};

// ─── Config ───────────────────────────────────────────────────────────────────
const STORES = [
  { index: 0,  id: "60a8e2ef88386f4c0c75a58a" },
  { index: 1,  id: "60a8edb84fa7cd28f240661e" },
  { index: 2,  id: "60a8eeb341bb70768c66ee3c" },
  { index: 3,  id: "60d824097c85f717826beddc" },
  { index: 4,  id: "60d824a78c164f16d826d169" },
  { index: 5,  id: "60dafa4e7705a663ef4f5268" },
  { index: 6,  id: "60db06852498467f361de7c7" },
  { index: 7,  id: "60e5d82a66b5e63019240702" },
  { index: 8,  id: "6145986916e3af4b7e7a5785" },
  { index: 9,  id: "619e1f0f19ee3b40ed5119a8" },
  { index: 10, id: "61a33bf4733ee01e5033a796" },
  { index: 11, id: "6204d056fa0f3b04ce24cb3a" },
  { index: 12, id: "668fa2f13dcf74671f035983" },
  { index: 13, id: "675829dd094af474350308b4" },
];

const TTL = 5 * 60 * 1000; // 5 minutes

const computeTotalFromPOs = (purchaseOrders) =>
  purchaseOrders.reduce((sum, po) => {
    if (!Array.isArray(po.po_inv_items)) return sum;
    return sum + po.po_inv_items.reduce((itemSum, item) => {
      const qty   = Number(item.quantity)      || 0;
      const price = Number(item.price_used)    || 0;
      const tax   = Number(item.total_tax_used) || 0;
      return itemSum + qty * price + tax;
    }, 0);
  }, 0);

// ─── Provider ─────────────────────────────────────────────────────────────────
export const PurchaseOrdersProvider = ({ children, endpoints = {} }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const { get, set } = useAppCache();

  // ✅ Freeze endpoints in a ref — avoids re-triggering the effect when the
  //    parent renders and passes a fresh object literal each time.
  const endpointsRef = useRef(endpoints);
  useEffect(() => { endpointsRef.current = endpoints; });

  const [stores, setStores]           = useState({});
  const [goods, setGoods]             = useState([]);
  const [categories, setCategories]   = useState([]);
  const [suppliers, setSuppliers]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    // Capture stable references at mount time — get/set come from useRef
    // inside useCache, so they are stable across renders.
    const cacheGet = get;
    const cacheSet = set;
    const base     = baseUrl;

    const fetchStore = async ({ index, id }) => {
      const cacheKey = `alfaroojfresh:po:store-${index}`;
      const cached   = cacheGet(cacheKey);
      if (cached) {
        setStores((prev) => ({ ...prev, [index]: cached }));
        return;
      }

      const url =
        endpointsRef.current[`endpoint${index}`] ||
        `${base}/api/api-farooj-index/combined-${index}`;

      try {
        const { data } = await axios.get(url);
        const orders = Object.values(data)
          .filter((s) => s?.data)
          .flatMap((s) =>
            s.data.map((po) => ({ ...po, storeIndex: index, storeId: id }))
          );
        const entry = { orders, total: data.total || computeTotalFromPOs(orders) };
        cacheSet(cacheKey, entry, TTL);
        setStores((prev) => ({ ...prev, [index]: entry }));
      } catch (err) {
        console.warn(`Store ${index} fetch failed:`, err.message);
        setStores((prev) => ({ ...prev, [index]: { orders: [], total: 0 } }));
      }
    };

    const fetchList = async (cacheKey, url, setter, errorMsg) => {
      const cached = cacheGet(cacheKey);
      if (cached) { setter(cached); return; }
      try {
        const { data } = await axios.get(url);
        const list = data.flatMap((item) => item.data || []);
        cacheSet(cacheKey, list, TTL);
        setter(list);
      } catch (err) {
        console.error(errorMsg, err);
        setError(errorMsg);
        setter([]);
      }
    };

    const run = async () => {
      setLoading(true);
      await Promise.all([
        fetchList("alfaroojfresh:goods",      `${base}/api/api-farooj-index/goods`,      setGoods,      "Failed to fetch goods."),
        fetchList("alfaroojfresh:categories", `${base}/api/api-farooj-index/categories`, setCategories, "Failed to fetch categories."),
        fetchList("alfaroojfresh:suppliers",  `${base}/api/api-farooj-index/combined`,   setSuppliers,  "Failed to fetch suppliers."),
        ...STORES.map(fetchStore),
      ]);
      setLoading(false);
    };

    run();
    // ✅ get/set are stable (wrapped in useCallback with no deps inside useCache)
    // ✅ endpoints is accessed via ref, not listed as a dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, get, set]);

  // ── Derived values ──────────────────────────────────────────────────────────
  const allPurchaseOrders = Object.values(stores).flatMap((s) => s?.orders ?? []);
  const grandTotal        = Object.values(stores).reduce((sum, s) => sum + (s?.total ?? 0), 0);
  const storeAt           = (i) => stores[i] ?? { orders: [], total: 0 };

  return (
    <PurchaseOrdersContext.Provider
      value={{
        ...Object.fromEntries(STORES.map(({ index }) => [`purchaseOrders${index}`, storeAt(index).orders])),
        ...Object.fromEntries(STORES.map(({ index }) => [`total${index}`,          storeAt(index).total])),
        allPurchaseOrders,
        grandTotal,
        stores,
        goods,
        categories,
        suppliers,
        loading,
        error,
      }}
    >
      {children}
    </PurchaseOrdersContext.Provider>
  );
};